import { mediaQueryLarge, isMobileBreakpoint } from "@theme/utilities";

// Accordion
// Extends HTMLElement so parent components can access its refs.
class AccordionCustom extends HTMLElement {
  #controller = new AbortController();
  /**
   * Handles the click event.
   * @param {Event} event - The event.
   */
  handleClick = event => {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    // Stop default behaviour from the browser
    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
    }
  };

  /**
   * Closes sibling accordions when this accordion belongs to an exclusive group.
   */
  #handleToggle = () => {
    if (!this.details.open) {
      return;
    }

    const group = this.closest("[data-accordion-exclusive]");
    if (!group) {
      return;
    }

    const openDetails = group.querySelectorAll(
      ":scope > accordion-custom > details[open], :scope > variant-fragment-component > accordion-custom > details[open]",
    );

    for (const details of openDetails) {
      if (details instanceof HTMLDetailsElement && details !== this.details) {
        details.open = false;
      }
    }
  };

  /**
   * Handles the media query change event.
   */
  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState();
  };

  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector("details");

    if (!(details instanceof HTMLDetailsElement)) {
      throw new TypeError("Details element not found");
    }

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector("summary");

    if (!(summary instanceof HTMLElement)) {
      throw new TypeError("Summary element not found");
    }

    return summary;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === "true";
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === "true";
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === "true";
  }

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.#setDefaultOpenState();

    this.addEventListener("keydown", this.#handleKeyDown, { signal });
    this.summary.addEventListener("click", this.handleClick, { signal });
    this.details.addEventListener("toggle", this.#handleToggle, { signal });
    mediaQueryLarge.addEventListener("change", this.#handleMediaQueryChange, { signal });
  }

  /**
   * Handles the disconnect event.
   */
  disconnectedCallback() {
    // Disconnect all the event listeners
    this.#controller.abort();
  }

  /**
   * Sets the default open state of the accordion based on the `open-by-default-on-mobile` and `open-by-default-on-desktop` attributes.
   */
  #setDefaultOpenState() {
    const isMobile = isMobileBreakpoint();

    this.details.open =
      (isMobile && this.hasAttribute("open-by-default-on-mobile")) ||
      (!isMobile && this.hasAttribute("open-by-default-on-desktop"));
  }

  /**
   * Handles keydown events for the accordion
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    // Close the accordion when used as a menu
    if (event.key !== "Escape" || !this.#closeWithEscape) {
      return;
    }

    event.preventDefault();

    this.details.open = false;
    this.summary.focus();
  }
}

if (!customElements.get("accordion-custom")) {
  customElements.define("accordion-custom", AccordionCustom);
}
